/**
 * Two-axis decoration status derivation helpers -- Phase 3.1
 * Decoration Detail page.
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 3.1 Decoration Detail skeleton + status header + matrix.
 *
 * ADR 0042: the library/usage axis (Draft / In use / Archived) and the
 * per-client approval axis (Not sent / Digitizing / Awaiting / Changes
 * requested / Approved) MUST never merge. These helpers compute the
 * library/usage pill state from the artwork's ``lifecycle_state`` +
 * the live where-used count. The per-client approval rollup lives on
 * the matrix payload's ``summary`` field; see ``approvalSummaryText``
 * below for the at-a-glance string.
 *
 * Why a pure helper (not a component method): so we can table-drive
 * the state space with vitest. The product editor's status chip and
 * the Decoration Library list both consume the same derivation; this
 * file is the single source.
 */

// Canonical usage-pill states for the header. ``key`` is the data
// value, ``label`` is the display text. The header uses the label for
// "Draft" / "Archived" and overrides for "In use (N)" using the
// where-used count (the count cannot be derived from the lifecycle
// state alone).
export const USAGE_PILL_STATES = Object.freeze({
    DRAFT: 'draft',
    IN_USE: 'in_use',
    ARCHIVED: 'archived',
})

const USAGE_LABELS = Object.freeze({
    [USAGE_PILL_STATES.DRAFT]: 'Draft',
    [USAGE_PILL_STATES.IN_USE]: 'In use',
    [USAGE_PILL_STATES.ARCHIVED]: 'Archived',
})

/**
 * Two-axis state derivation -- pure function over (lifecycle_state,
 * where_used_count).
 *
 * Truth table (per ADR 0042):
 *
 *   | lifecycle_state | where_used_count | usage pill state |
 *   |-----------------|------------------|------------------|
 *   | archived        | any              | Archived         |
 *   | any non-archived| 0                | Draft            |
 *   | any non-archived| >= 1             | In use           |
 *
 * Rationale: ``archived`` is terminal -- a bound artwork that's been
 * archived is still "archived" (the warning banner fires separately).
 * For every other lifecycle_state value (draft / in_review / approved /
 * rejected), the user-facing "is this in use?" question is answered by
 * the live where-used count, NOT by whether digitization / approval
 * succeeded. A draft artwork bound to a product is "In use" from the
 * Library's perspective; a fully-approved artwork bound to zero
 * products is still "Draft" (it has no live consumers).
 *
 * The ``ARTWORK_STATES`` whitelist on the model is
 * ``(draft, in_review, approved, rejected, archived)`` -- we treat any
 * unknown / unexpected value as DRAFT (defensive default for legacy or
 * future state additions) so the header never renders an empty pill.
 *
 * @param {string} lifecycleState - artwork.lifecycle_state from the API
 * @param {number} whereUsedCount - usage.campaign_products.length
 * @returns {string} one of USAGE_PILL_STATES
 */
export function deriveUsagePillState(lifecycleState, whereUsedCount) {
    if (lifecycleState === 'archived') {
        return USAGE_PILL_STATES.ARCHIVED
    }
    const count = Number(whereUsedCount) || 0
    if (count >= 1) {
        return USAGE_PILL_STATES.IN_USE
    }
    return USAGE_PILL_STATES.DRAFT
}

/**
 * Display label for the usage pill, including the "(N)" count for the
 * "In use" state per the spec ("In use (N)").
 *
 * @param {string} pillState - one of USAGE_PILL_STATES
 * @param {number} whereUsedCount - the live binding count
 * @returns {string} e.g. "Draft" / "In use (3)" / "Archived"
 */
export function usagePillLabel(pillState, whereUsedCount) {
    if (pillState === USAGE_PILL_STATES.IN_USE) {
        const count = Number(whereUsedCount) || 0
        return `In use (${count})`
    }
    return USAGE_LABELS[pillState] ?? USAGE_LABELS[USAGE_PILL_STATES.DRAFT]
}

/**
 * Approval at-a-glance summary text for the header pill.
 *
 * Reads from the matrix endpoint's ``summary`` payload (canonical
 * shape: ``{approved, awaiting, changes_requested, total_clients}``).
 *
 * Format: "N approved | M awaiting | K need changes" -- middle dot
 * separator matches the design HTML (header.ah-pills "axis approval"
 * spans). When all three are zero, returns "No approval requests sent
 * yet." (matches the matrix-table empty state copy for consistency).
 *
 * @param {object} summary
 * @returns {string}
 */
export function approvalSummaryText(summary) {
    if (!summary) return 'No approval requests sent yet.'
    const approved = Number(summary.approved) || 0
    const awaiting = Number(summary.awaiting) || 0
    const changes = Number(summary.changes_requested) || 0
    if (approved === 0 && awaiting === 0 && changes === 0) {
        return 'No approval requests sent yet.'
    }
    return (
        `${approved} approved · ${awaiting} awaiting · ` +
        `${changes} need changes`
    )
}

/**
 * Out-of-date inline label per P3-Q13: "v{N} approved (v{M} current)".
 *
 * @param {number} approvedVersion - the latest version_number the client approved
 * @param {number} currentVersion - the artwork's current version_number
 * @returns {string}
 */
export function outOfDateLabel(approvedVersion, currentVersion) {
    return `v${approvedVersion} approved (v${currentVersion} current)`
}

/**
 * Short, locale-aware date ("Mar 4, 2026") for a facts line; "" for a
 * missing or unparseable value so callers can drop the part.
 *
 * @param {string|null|undefined} isoString
 * @returns {string}
 */
export function shortDateLabel(isoString) {
    if (!isoString) return ''
    const d = new Date(isoString)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

/**
 * One-line digitizer state for an artwork's facts line. The states are the
 * backend's digitization vocabulary (art_prep: not_sent / digitizing /
 * digitized / failed), the same keys the per-size Digitization card reads,
 * rolled up per artwork by the library payload's ``digitizer`` object.
 *
 * @param {{state: string, vendor_name?: string, at?: string}|null} digitizer
 * @returns {string} "" when there is no digitizer or the state is unknown
 */
export function digitizerStateLabel(digitizer) {
    if (!digitizer) return ''
    // ``partial``: the state shown is not every size's (other sizes differ).
    const some = digitizer.partial ? ' (some sizes)' : ''
    switch (digitizer.state) {
        case 'not_sent':
            return `Not sent to digitizer${some}`
        case 'digitizing':
            return `Awaiting digitizer${some}`
        case 'failed':
            return `Digitizer failed${some}`
        case 'digitized': {
            const when = shortDateLabel(digitizer.at)
            const parts = ['Digitized']
            if (when) parts.push(when)
            if (digitizer.vendor_name) parts.push(`by ${digitizer.vendor_name}`)
            return parts.join(' ')
        }
        default:
            return ''
    }
}
